import * as React from 'react';
import {FormControl, MenuItem, Select, Stack, type SelectChangeEvent} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlaceIcon from '@mui/icons-material/Place';
import {useTranslation} from 'react-i18next';
import {ALL_BRANCHES_VALUE, usePeriod} from '../../context/PeriodContext';
import {formatPeriodLabel} from '../../utils/periodLabel';

/**
 * Mobile-only period/branch filter row (see mobile dashboard design reference). On mobile the
 * account menu stays in the top-right header bar (see DashboardToolbarActions), so these two
 * selects - inline in that same toolbar on desktop (PeriodBranchToolbarSelector) - get their own
 * full-width row above the page content instead of competing for space with the bell/avatar.
 * Rendered once in NutoUiMain so every page picks it up consistently.
 */
export function MobilePeriodBranchFilterBar(): React.ReactElement | null {
    const {t} = useTranslation();
    const {
        allPeriods, branches, optionsLoading,
        selectedPeriod, selectedPeriodValue, setPeriodAndValue,
        dashboardBranch, setDashboardBranch,
    } = usePeriod();

    if (optionsLoading && allPeriods.length === 0 && branches.length === 0) {
        return null;
    }

    const handlePeriodChange = (event: SelectChangeEvent) => {
        const period = allPeriods.find((p) => p.periodKey === event.target.value);
        setPeriodAndValue(period?.periodKey ?? null, period?.value ?? null);
    };

    const handleBranchChange = (event: SelectChangeEvent) => {
        const branchId = event.target.value;
        const branchName = branchId === ALL_BRANCHES_VALUE
            ? t('common.filters.all')
            : branches.find((b) => b.id === branchId)?.name ?? '';
        setDashboardBranch(branchId, branchName);
    };

    return (
        <Stack direction="row" spacing={1.5} sx={{width: '100%', mb: 3}}>
            <FormControl fullWidth size="small">
                <Select
                    displayEmpty
                    value={selectedPeriod ?? ''}
                    onChange={handlePeriodChange}
                    startAdornment={<CalendarTodayIcon fontSize="small" sx={{mr: 1, color: 'text.secondary'}}/>}
                    renderValue={() => selectedPeriod
                        ? formatPeriodLabel(t, selectedPeriod, selectedPeriodValue)
                        : t('common.filters.selectPeriod')}
                    sx={{borderRadius: 2}}
                >
                    {allPeriods.map((period) => (
                        <MenuItem key={period.periodKey} value={period.periodKey}>
                            {formatPeriodLabel(t, period.periodKey, period.value)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth size="small">
                <Select
                    value={dashboardBranch}
                    onChange={handleBranchChange}
                    startAdornment={<PlaceIcon fontSize="small" sx={{mr: 1, color: 'text.secondary'}}/>}
                    sx={{borderRadius: 2}}
                >
                    <MenuItem value={ALL_BRANCHES_VALUE}>{t('common.filters.all')}</MenuItem>
                    {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    );
}
